<template>
  <div>
    <div class="tile is-parent pl-1">
      <article class="tile is-child box">
        <div class="columns is-flex is-align-items-center">
          <div class="column">
            <p class="title">FlexiQuote Module</p>
          </div>
          <div v-if="!hasError && showSavingStatus && !toastNotificationEnabled"
            class="column is-flex is-justify-content-flex-end is-align-items-center">
            <span v-if="saving"
              class="button is-loading is-white"
              style="height: 27px;" />
            <span v-else
              class="text has-text-success">
              <span class="mso mso-18px">check_circle</span>
            </span>
            <span class="is-size-6"
              :class="saving ? 'has-text-warning' : 'has-text-success'">{{ savingStatus }}</span>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <PermissionTree :permission-records="permissionRecords" />
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'
import PermissionTree from '../components/PermissionTree.vue'
import StoreMixin from './storeMixin'
import SystemModuleService from './systemModuleService'

export default {
  name: 'SystemModulesDetail',
  components: {
    PermissionTree
  },
  mixins: [StoreMixin],
  data() {
    return {
      showAccessibleItems: true,
      permissionRecords: [],
      isResetting: false
    }
  },
  computed: {
    hasDirtyRecords() {
      return this.snapshotDiff && this.snapshotDiff.length > 0
    }
  },
  watch: {
    permissionRecords: {
      handler: function (val) {
        if (this.isResetting) return

        this.saveSnapshot({ entity: _cloneDeep(val) })
        if (typeof this.autoUpdate === 'function') this.autoUpdate(val)
      },
      deep: true
    }
  },
  async created() {
    this.$showSpinner()
    await this.getAllPermissionRecords()
    this.initializeAutoUpdate()
    this.$hideSpinner()
  },
  methods: {
    async getAllPermissionRecords() {
      const records = await SystemModuleService.getAllPermissionRecords()
      this.isResetting = true
      this.permissionRecords = records
      this.disableNodePermissionAndChildren('Accounts')
      await this.$nextTick()
      this.saveInitialSnapshots({ entity: _cloneDeep(this.permissionRecords) })
      this.isResetting = false
    },
    disableAutoUpdate() {
      if (typeof this.autoUpdate === 'function' && typeof this.autoUpdate.cancel === 'function') {
        this.autoUpdate.cancel()
      }
      this.autoUpdate = () => {}
    },
    initializeAutoUpdate() {
      this.autoUpdate = _debounce(async (value) => {
        this.hasDirtyRecords && (await this.updatePermissionRecords(value))
      }, this.debounceDelay)
    },
    async updatePermissionRecords(permissionRecords) {
      if (!this.hasDirtyRecords) return

      this.setSavingStatus(true)

      await this.$nextTick()
      await new Promise((resolve) => setTimeout(resolve, 100))

      const changedRecords = this.getChangedRecords()

      const isSuccess = await SystemModuleService.updatePermissionRecords(changedRecords)
      if (isSuccess) {
        this.isResetting = true
        this.saveInitialSnapshots({ entity: _cloneDeep(permissionRecords) })
        this.isResetting = false
      } else {
        this.setErrorStatus(true)
      }
      this.setSavingStatus(false)

      if (this.toastNotificationEnabled) {
        this.openToastNotification()
      }
    },
    getChangedRecords() {
      if (!this.snapshotDiff || this.snapshotDiff.length === 0) {
        return []
      }

      const changedIndices = new Set()

      this.snapshotDiff.forEach((diff) => {
        if (diff.path && diff.path.length > 0) {
          const recordIndex = diff.path[0]
          if (typeof recordIndex === 'number') {
            changedIndices.add(recordIndex)
          }
        }
      })

      const changedRecords = Array.from(changedIndices).map((index) => {
        return this.permissionRecords[index]
      })

      return changedRecords
    },
    disableNodePermissionAndChildren(nodeName) {
      const parentNode = this.permissionRecords.find((record) => record.name === nodeName)
      if (!parentNode) {
        return
      }
      const disableRecordAndChildren = (parentId) => {
        const children = this.permissionRecords.filter((record) => record.parentID === parentId)

        children.forEach((child) => {
          this.$set(child, 'disabled', true)
          disableRecordAndChildren(child.permissionRecordID)
        })
      }
      this.$set(parentNode, 'disabled', true)
      disableRecordAndChildren(parentNode.permissionRecordID)
    }
  },
  async beforeRouteLeave(to, from, next) {
    this.disableAutoUpdate()
    this.toastNotificationEnabled = true
    await this.updatePermissionRecords(this.permissionRecords)
    this.clearSnapshots()
    this.toastNotificationEnabled = false
    next()
  }
}
</script>

<style scoped>
</style>

